import Guid from "common/values/guid/guid";
import Address, { AddressJSON } from "common/values/address/address";

export interface CompanyProfileJSON {
  name: string;
  description?: string;
  location?: AddressJSON;
  yearEstablished?: number;
  website?: string;
  numberOfEmployees?: number;
  categories: string[];
  avatarId?: string;
  contactId?: string;
  contactUserId?: string;
}

export default class CompanyProfile {
  name: string;
  description?: string;
  location?: Address;
  yearEstablished?: number;
  website?: string;
  numberOfEmployees?: number;
  categories: string[] = [];
  avatarId?: Guid;
  contactId?: Guid;
  contactUserId?: Guid;
  type?: string;

  constructor(
    name: string,
    description?: string,
    location?: Address,
    yearEstablished?: number,
    website?: string,
    numberOfEmployees?: number,
    categories?: string[],
    avatarId?: Guid,
    contactId?: Guid,
    contactUserId?: Guid,
  ) {
    this.name = name;
    this.description = description;
    this.location = location;
    this.yearEstablished = yearEstablished;
    this.website = website;
    this.numberOfEmployees = numberOfEmployees;
    this.categories = categories ?? [];
    this.avatarId = avatarId;
    this.contactId = contactId;
    this.contactUserId = contactUserId;
  }

  clone(): CompanyProfile {
    const newProfile = new CompanyProfile(this.name);
    Object.assign(newProfile, this);
    return newProfile;
  }

  updateName(name: string): CompanyProfile {
    return new CompanyProfile(
      name,
      this.description,
      this.location,
      this.yearEstablished,
      this.website,
      this.numberOfEmployees,
      this.categories,
      this.avatarId,
      this.contactId,
      this.contactUserId
    );
  }

  updateCategories(categories: string[]): CompanyProfile {
    return new CompanyProfile(
      this.name,
      this.description,
      this.location,
      this.yearEstablished,
      this.website,
      this.numberOfEmployees,
      categories,
      this.avatarId,
      this.contactId,
      this.contactUserId
    );
  }

  updateContactId(contactId: Guid): CompanyProfile {
    return new CompanyProfile(
      this.name,
      this.description,
      this.location,
      this.yearEstablished,
      this.website,
      this.numberOfEmployees,
      this.categories,
      this.avatarId,
      contactId,
      this.contactUserId
    );
  }

  updateYearEstablished(yearEstablished?: number): CompanyProfile {
    return new CompanyProfile(
      this.name,
      this.description,
      this.location,
      yearEstablished,
      this.website,
      this.numberOfEmployees,
      this.categories,
      this.avatarId,
      this.contactId,
      this.contactUserId
    );
  }

  updateNumberOfEmployees(numberOfEmployees?: number): CompanyProfile {
    return new CompanyProfile(
      this.name,
      this.description,
      this.location,
      this.yearEstablished,
      this.website,
      numberOfEmployees,
      this.categories,
      this.avatarId,
      this.contactId,
      this.contactUserId
    );
  }

  updateWebsite(website?: string): CompanyProfile {
    return new CompanyProfile(
      this.name,
      this.description,
      this.location,
      this.yearEstablished,
      website,
      this.numberOfEmployees,
      this.categories,
      this.avatarId,
      this.contactId,
      this.contactUserId
    );
  }

  updateDescription(description?: string): CompanyProfile {
    return new CompanyProfile(
      this.name,
      description,
      this.location,
      this.yearEstablished,
      this.website,
      this.numberOfEmployees,
      this.categories,
      this.avatarId,
      this.contactId,
      this.contactUserId
    );
  }

  updateLocation(location: Address | null): CompanyProfile {
    return new CompanyProfile(
      this.name,
      this.description,
      location ?? undefined,
      this.yearEstablished,
      this.website,
      this.numberOfEmployees,
      this.categories,
      this.avatarId,
      this.contactId,
      this.contactUserId
    );
  }

  public static fromJSON(json: CompanyProfileJSON): CompanyProfile {
    const profile = new CompanyProfile(json.name);
    profile.description = json.description;
    profile.location = json.location ? Address.fromJSON(json.location) : undefined;
    profile.yearEstablished = json.yearEstablished;
    profile.website = json.website;
    profile.numberOfEmployees = json.numberOfEmployees;
    profile.categories = json.categories;
    profile.avatarId = json.avatarId ? Guid.fromJSON(json.avatarId) : undefined;
    profile.contactId = json.contactId ? Guid.fromJSON(json.contactId) : undefined;
    profile.contactUserId = json.contactUserId ? Guid.fromJSON(json.contactUserId) : undefined;
    return profile;
  }

  public toJSON(): CompanyProfileJSON {
    return {
      name: this.name,
      description: this.description,
      location: this.location,
      yearEstablished: this.yearEstablished,
      website: this.website,
      numberOfEmployees: this.numberOfEmployees,
      categories: this.categories,
      avatarId: this.avatarId?.toJSON(),
      contactId: this.contactId?.toJSON(),
      contactUserId: this.contactUserId?.toJSON()
    };
  }
}
